import { useEffect } from 'react';

import { NATIVE_INSET_EVENT, probeRequested, resolveInset } from './deviceBottomInset';

/**
 * Publishes the height of an OS overlay that covers the bottom of the viewport as
 * --device-bottom-inset. The Optoma panel's launcher dock is drawn above the browser and the
 * viewport never learns about it, so no vh/dvh unit can see it - the height has to be declared.
 * The Android wrapper reports the real bar height instead, and re-publishing on its `nativeinsets`
 * event is what closes the boot race when the first dispatch beat this document into existence.
 */
export function useDeviceBottomInset(): void {
  useEffect(() => {
    const publish = () => {
      document.documentElement.style.setProperty('--device-bottom-inset', `${resolveInset().inset}px`);
    };
    publish();

    // the wrapper's first dispatch can precede this document, so the event - not the mount value - is authoritative
    window.addEventListener(NATIVE_INSET_EVENT, publish);

    // dynamic so the probe is its own chunk and an unarmed build never loads it
    let dispose: (() => void) | undefined;
    let dropped = false;
    if (probeRequested()) {
      import('./viewportProbe').then((mod) => {
        if (dropped) return;
        dispose = mod.mountViewportProbe(publish);
      });
    }

    return () => {
      dropped = true;
      window.removeEventListener(NATIVE_INSET_EVENT, publish);
      if (dispose) dispose();
      document.documentElement.style.removeProperty('--device-bottom-inset');
    };
  }, []);
}
